import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Paper, Grid, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchHeroes, fetchHeroStats } from '../services/dataService';

const WinRate = () => {
  const { t } = useLanguage();
  const [heroes, setHeroes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [gameMode, setGameMode] = useState('quickPlay');
  const [competitiveRank, setCompetitiveRank] = useState('competitiveSummary');
  const [sortBy, setSortBy] = useState('winRate');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [heroesData, heroStatsData] = await Promise.all([
          fetchHeroes(),
          fetchHeroStats()
        ]);
        
        // Get the appropriate data based on game mode and rank
        let statsData = [];
        if (gameMode === 'quickPlay') {
          statsData = heroStatsData.quickPlay || [];
        } else {
          // For competitive mode, use the selected rank or summary
          statsData = heroStatsData[competitiveRank] || [];
        }
        
        // Combine hero data with stats data
        const heroesWithWinRate = heroesData.map(hero => {
          // Find stats for this hero
          const heroStats = statsData.find(stats => stats.name === hero.name);
          
          if (heroStats) {
            // Parse percentage strings to numbers
            const winRateValue = parseFloat(heroStats.winRate) || 50;
            const pickRateValue = parseFloat(heroStats.pickRate) || 10;
            
            return {
              ...hero,
              winRate: winRateValue.toFixed(1),
              pickRate: pickRateValue.toFixed(1),
              banRate: '0.0', // Ban rate not available in new data structure
              role: heroStats.role.toUpperCase() // Ensure role is uppercase to match filters
            };
          } else {
            // Fallback to placeholder data if no stats available
            return {
              ...hero,
              winRate: '50.0', // Default win rate
              pickRate: '10.0', // Default pick rate
              banRate: '0.0',   // Default ban rate
            };
          }
        });
        
        setHeroes(heroesWithWinRate);
      } catch (error) {
        console.error("Error loading hero data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [gameMode, competitiveRank]);

  const handleRoleFilterChange = (event) => {
    setRoleFilter(event.target.value);
  };

  const handleGameModeChange = (event) => {
    setGameMode(event.target.value);
    // Reset competitive rank when switching game modes
    if (event.target.value === 'quickPlay') {
      setCompetitiveRank('competitiveSummary');
    }
  };

  const handleCompetitiveRankChange = (event) => {
    setCompetitiveRank(event.target.value);
  };

  const handleSortChange = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  const filteredHeroes = heroes.filter(hero => {
    if (!roleFilter) return true;
    return hero.role === roleFilter;
  });

  const sortedHeroes = [...filteredHeroes].sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'name') {
      comparison = a.name.localeCompare(b.name);
    } else {
      comparison = parseFloat(b[sortBy]) - parseFloat(a[sortBy]);
    }
    return sortDirection === 'asc' ? -comparison : comparison;
  });

  const getRoleColor = (role) => {
    switch(role) {
      case 'VANGUARD': return '#3498db';
      case 'DUELIST': return '#e74c3c';
      case 'STRATEGIST': return '#2ecc71';
      default: return '#fbdb2b';
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          backgroundColor: '#2b3340',
          flexDirection: 'column',
          gap: 3
        }}
      >
        <CircularProgress sx={{ color: '#fbdb2b' }} size={60} />
        <Typography variant="h5" color="white">
          {t('loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#2b3340', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          align="center"
          sx={{ 
            color: 'white',
            fontWeight: 'bold',
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            mb: 4,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -10,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80px',
              height: '4px',
              backgroundColor: '#fbdb2b',
              borderRadius: '2px'
            }
          }}
        >
          {t('winRate')}
        </Typography>

        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 4, 
            backgroundColor: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel 
                  id="game-mode-select-label"
                  sx={{ 
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-focused': {
                      color: '#fbdb2b'
                    }
                  }}
                >
                  {t('gameMode')}
                </InputLabel>
                <Select
                  labelId="game-mode-select-label"
                  value={gameMode}
                  label={t('gameMode')}
                  onChange={handleGameModeChange}
                  sx={{ 
                    color: 'white',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#fbdb2b',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.5)',
                    },
                    '.MuiSvgIcon-root': {
                      color: 'rgba(255,255,255,0.7)',
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#2b3340',
                        color: 'white',
                        '& .MuiMenuItem-root': {
                          '&:hover': {
                            backgroundColor: 'rgba(251, 219, 43, 0.1)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(251, 219, 43, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(251, 219, 43, 0.3)',
                            }
                          }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="quickPlay">{t('quickPlay')}</MenuItem>
                  <MenuItem value="competitive">{t('competitive')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel 
                  id="role-select-label"
                  sx={{ 
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-focused': {
                      color: '#fbdb2b'
                    }
                  }}
                >
                  {t('role')}
                </InputLabel>
                <Select
                  labelId="role-select-label"
                  value={roleFilter}
                  label={t('role')}
                  onChange={handleRoleFilterChange}
                  sx={{ 
                    color: 'white',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#fbdb2b',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.5)',
                    },
                    '.MuiSvgIcon-root': {
                      color: 'rgba(255,255,255,0.7)',
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#2b3340',
                        color: 'white',
                        '& .MuiMenuItem-root': {
                          '&:hover': {
                            backgroundColor: 'rgba(251, 219, 43, 0.1)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(251, 219, 43, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(251, 219, 43, 0.3)',
                            }
                          }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>{t('allRoles')}</em>
                  </MenuItem>
                  <MenuItem value="VANGUARD">{t('vanguard')}</MenuItem>
                  <MenuItem value="DUELIST">{t('duelist')}</MenuItem>
                  <MenuItem value="STRATEGIST">{t('strategist')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl 
                fullWidth 
                size="small" 
                disabled={gameMode === 'quickPlay'}
              >
                <InputLabel 
                  id="rank-select-label"
                  sx={{ 
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-focused': {
                      color: '#fbdb2b'
                    }
                  }}
                >
                  {t('rank')}
                </InputLabel>
                <Select
                  labelId="rank-select-label"
                  value={competitiveRank}
                  label={t('rank')}
                  onChange={handleCompetitiveRankChange}
                  sx={{ 
                    color: 'white',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#fbdb2b',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.5)',
                    },
                    '.MuiSvgIcon-root': {
                      color: 'rgba(255,255,255,0.7)',
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#2b3340',
                        color: 'white',
                        '& .MuiMenuItem-root': {
                          '&:hover': {
                            backgroundColor: 'rgba(251, 219, 43, 0.1)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(251, 219, 43, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(251, 219, 43, 0.3)',
                            }
                          }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="competitiveSummary">{t('competitiveSummary')}</MenuItem>
                  <MenuItem value="competitiveBronze">{t('bronze')}</MenuItem>
                  <MenuItem value="competitiveSilver">{t('silver')}</MenuItem>
                  <MenuItem value="competitiveGold">{t('gold')}</MenuItem>
                  <MenuItem value="competitivePlatinum">{t('platinum')}</MenuItem>
                  <MenuItem value="competitiveDiamond">{t('diamond')}</MenuItem>
                  <MenuItem value="competitiveGrandmaster">{t('grandmaster')}</MenuItem>
                  <MenuItem value="competitiveCelestialAndAbove">{t('celestial')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer 
          component={Paper}
          sx={{ 
            backgroundColor: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
                <TableCell 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  {t('hero')}
                </TableCell>
                <TableCell 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  {t('role')}
                </TableCell>
                <TableCell 
                  onClick={() => handleSortChange('winRate')}
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#fbdb2b'
                    }
                  }}
                >
                  {t('winRate')} {getSortIcon('winRate')}
                </TableCell>
                <TableCell 
                  onClick={() => handleSortChange('pickRate')}
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#fbdb2b'
                    }
                  }}
                >
                  {t('pickRate')} {getSortIcon('pickRate')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedHeroes.map((hero) => (
                <TableRow 
                  key={hero.id}
                  sx={{ 
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'rgba(0,0,0,0.1)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(251, 219, 43, 0.05)'
                    }
                  }}
                >
                  <TableCell 
                    sx={{ 
                      color: 'white',
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    {hero.image && (
                      <Box
                        component="img"
                        src={hero.image}
                        alt={hero.name}
                        sx={{
                          width: 40,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 1
                        }}
                      />
                    )}
                    {hero.name}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      color: getRoleColor(hero.role),
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      fontWeight: 'medium'
                    }}
                  >
                    {hero.role === 'VANGUARD' ? t('vanguard') : 
                     hero.role === 'DUELIST' ? t('duelist') : 
                     hero.role === 'STRATEGIST' ? t('strategist') : hero.role}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      color: parseFloat(hero.winRate) > 52 ? '#4caf50' : 
                             parseFloat(hero.winRate) < 48 ? '#f44336' : 'white',
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      fontWeight: 'bold'
                    }}
                  >
                    {hero.winRate}%
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      color: 'white',
                      borderBottom: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    {hero.pickRate}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {t('dataUpdated')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1 }}>
            {t('copyright')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default WinRate;